import React from "react";
import styled from "styled-components";
import tw from "twin.macro";
import { SectionHeading } from "components/misc/Headings";
import { useTranslation } from "react-i18next"; // Import the useTranslation hook
import { ReactComponent as CheckboxIcon } from "feather-icons/dist/icons/check-circle.svg";
import { ReactComponent as SvgDecoratorBlob1 } from "images/dot-pattern.svg";
import { SectionDescription } from "components/misc/Typography";
import sesImg from "images/itservice/v2/ses.jpg";
import SystemEngineeringMobileView from "./SystemEngineeringMobileView";

const Container = tw.div`relative`;
const Heading = tw(SectionHeading)`text-ncc text-left`;
const ThreeColumn = tw.div`grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-20 max-w-screen-xl py-2`;

const LeftColumn = tw.div`relative lg:col-span-1 text-left lg:pl-0 lg:pr-6`;
const MidColumn = tw.div`relative lg:col-span-1 text-center lg:text-left lg:px-6`;
const RightColumn = tw.div`relative lg:col-span-1 text-center lg:text-left lg:pl-6`;

const Subtitle = tw.h2`font-semibold text-sm mt-10`;
const FeatureList = tw.ul`mt-2 leading-loose`;
const Feature = tw.li`flex items-center`;
const FeatureIcon = tw(CheckboxIcon)`w-5 h-5 text-ncc-light`;
const FeatureText = tw.p`ml-2 text-sm font-medium text-gray-700`;

const Column = tw.div``;
const TextColumn = tw(Column)`mr-auto lg:mr-0 max-w-lg`;

const Row = tw.div`flex flex-col lg:flex-row justify-between items-center lg:pt-8 max-w-screen-2xl mx-auto sm:px-8`;

const ImageColumn = tw(Column)`ml-auto lg:mr-0 relative mt-24`;
const ImageContainer = tw.div`relative z-40 transform xl:-translate-x-24 xl:-translate-y-16`;
const Image = tw.img`max-w-full w-96 rounded-t sm:rounded relative z-20`;
const Offsetbackground = tw.div`absolute inset-0 bg-gray-300 rounded xl:-mb-8`;
const ImageDecoratorBlob = styled(SvgDecoratorBlob1)`
  ${tw`pointer-events-none z-10 absolute right-0 bottom-0 transform translate-x-10 translate-y-10 h-32 w-32 opacity-25 text-gray-900 fill-current`}
`;

const Description = tw(
  SectionDescription
)`mt-4 lg:text-base text-gray-700 max-w-lg`;

const DecoratorBlob1 = styled(SvgDecoratorBlob1)`
  ${tw`pointer-events-none opacity-5 absolute left-0 bottom-0 h-64 w-64 transform -translate-x-2/3 -z-10`}
`;

const imageDecoratorBlob = true;

export default ({ isXs = false }) => {
  if (isXs) return <SystemEngineeringMobileView />;
  const { t } = useTranslation(); // Initialize the useTranslation hook

  // Extracting localized content from the 'business.ses' path
  const infrastructure = t("business.ses.Infrastructure.content", {
    returnObjects: true,
  });
  const developmentLanguage = t("business.ses.development_lan.content", {
    returnObjects: true,
  });
  const environment = t("business.ses.Environment.content", {
    returnObjects: true,
  });

  return (
    <div id="system-engineering">
      <Container>
        <Row>
          <TextColumn>
            <Heading>{t("business.ses.heading")}</Heading>
            <Description>{t("business.ses.description")}</Description>
          </TextColumn>
          <ImageColumn>
            <ImageContainer>
              <Image src={sesImg} />
              {imageDecoratorBlob && <ImageDecoratorBlob />}
            </ImageContainer>
            <Offsetbackground />
          </ImageColumn>
        </Row>

        {/* Feature Columns */}
        <Row>
          <ThreeColumn>
            <LeftColumn>
              <Subtitle>{t("business.ses.development_lan.title")}</Subtitle>
              <FeatureList>
                {developmentLanguage.map((feature, index) => (
                  <Feature key={index}>
                    <FeatureIcon />
                    <FeatureText>{feature}</FeatureText>
                  </Feature>
                ))}
              </FeatureList>
            </LeftColumn>

            <MidColumn>
              <Subtitle>{t("business.ses.Infrastructure.title")}</Subtitle>
              <FeatureList>
                {infrastructure.map((feature, index) => (
                  <Feature key={index}>
                    <FeatureIcon />
                    <FeatureText>{feature}</FeatureText>
                  </Feature>
                ))}
              </FeatureList>
            </MidColumn>

            <RightColumn>
              <Subtitle>{t("business.ses.Environment.title")}</Subtitle>
              <FeatureList>
                {environment.map((feature, index) => (
                  <Feature key={index}>
                    <FeatureIcon />
                    <FeatureText>{feature}</FeatureText>
                  </Feature>
                ))}
              </FeatureList>
            </RightColumn>
          </ThreeColumn>
        </Row>
        <DecoratorBlob1 />
      </Container>
    </div>
  );
};
